import { useState, useEffect } from 'react';
import api from '../services/api';
import PageHeader from '../components/ui/PageHeader';
import Card from '../components/ui/Card';
import Badge from '../components/ui/Badge';
import Button from '../components/ui/Button';
import EmptyState from '../components/ui/EmptyState';
import SlicerMachineForm from '../components/SlicerMachineForm';
import ConfirmationModal from '../components/ConfirmationModal';
import { useToast } from '../hooks/useToast';
import {
  PlusIcon,
  PencilIcon,
  DocumentDuplicateIcon,
  TrashIcon,
  ServerIcon,
} from '@heroicons/react/24/outline';

export default function FleetMachines() {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingMachine, setEditingMachine] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const { showToast } = useToast();

  const fetchMachines = async () => {
    try {
      const res = await api.get('/auth/machine-models/machines-by-model');
      setGroups(res.data?.data || res.data || []);
    } catch {
      showToast('Failed to load machines', 'error');
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps -- fetch on mount only
  useEffect(() => { fetchMachines(); }, []);

  const handleSave = async (data) => {
    try {
      if (editingMachine) {
        await api.patch(`/auth/machines/${editingMachine.uid}`, data);
        showToast('Machine updated', 'success');
      } else {
        await api.post('/auth/machines', data);
        showToast('Machine created', 'success');
      }
      setShowForm(false);
      setEditingMachine(null);
      fetchMachines();
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to save machine', 'error');
    }
  };

  const handleDuplicate = async (machine) => {
    try {
      const response = await api.post(`/auth/machines/${machine.uid}/duplicate`);
      const duplicated = response.data.data || response.data;
      setEditingMachine(duplicated);
      setShowForm(true);
      fetchMachines();
      showToast('Machine duplicated. You can now customize it.', 'success');
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to duplicate', 'error');
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/auth/machines/${deleteTarget.uid}`);
      showToast('Machine deleted', 'success');
      setDeleteTarget(null);
      fetchMachines();
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to delete', 'error');
    }
  };

  const totalMachines = groups.reduce((sum, g) => sum + (g.machines?.length || 0), 0);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Machines"
        description={`${totalMachines} machine${totalMachines !== 1 ? 's' : ''} in your fleet`}
        actions={
          <Button icon={PlusIcon} onClick={() => { setEditingMachine(null); setShowForm(true); }}>
            Add Machine
          </Button>
        }
      />

      {loading ? (
        <p className="text-sm text-slate-500">Loading...</p>
      ) : groups.length === 0 ? (
        <EmptyState icon={ServerIcon} title="No machines yet" description="Add your first printer to get started" />
      ) : (
        groups.map((group) => (
          <div key={group.model?._id || group.model?.name}>
            <div className="flex items-center gap-3 mb-3">
              {group.model?.thumbnail?.url && (
                <img src={group.model.thumbnail.url} alt="" className="w-8 h-8 rounded object-cover" />
              )}
              <h2 className="text-sm font-semibold text-slate-700">
                {group.model?.name || group.model?.fullName || 'Unknown Model'}
              </h2>
              <Badge size="sm" variant="default">{group.machines?.length || 0}</Badge>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-6">
              {group.machines?.map((machine) => (
                <Card key={machine._id} className="!p-4">
                  <div className="flex items-start justify-between">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-slate-900 truncate">{machine.name}</p>
                      <p className="text-xs text-slate-500 mt-0.5">UID: {machine.uid}</p>
                    </div>
                    <Badge
                      variant={machine.enabled === false ? 'default' : machine.isBusy ? 'warning' : 'success'}
                      size="sm"
                      dot
                    >
                      {machine.enabled === false ? 'Disabled' : machine.isBusy ? 'Busy' : 'Idle'}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-1 mt-3 pt-3 border-t border-slate-100">
                    <button onClick={() => { setEditingMachine(machine); setShowForm(true); }} className="p-1.5 text-slate-400 hover:text-blue-600 rounded transition-colors" title="Edit">
                      <PencilIcon className="h-4 w-4" />
                    </button>
                    <button onClick={() => handleDuplicate(machine)} className="p-1.5 text-slate-400 hover:text-blue-600 rounded transition-colors" title="Duplicate">
                      <DocumentDuplicateIcon className="h-4 w-4" />
                    </button>
                    <button onClick={() => setDeleteTarget(machine)} className="p-1.5 text-slate-400 hover:text-red-600 rounded transition-colors" title="Delete">
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        ))
      )}

      {showForm && (
        <SlicerMachineForm
          machine={editingMachine}
          onSave={handleSave}
          onCancel={() => { setShowForm(false); setEditingMachine(null); }}
        />
      )}

      {deleteTarget && (
        <ConfirmationModal
          title="Delete Machine"
          message={`Are you sure you want to delete "${deleteTarget.name}"?`}
          onConfirm={handleDelete}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
    </div>
  );
}
